import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useCountry } from '../pages/CountryContext'; // Adjust the import path as necessary

import {
  getCountryCode,
  getRegionList,
  getDistrictList,
  getMunicipalityList,
} from "../utils/regionHelper";
import { dropdownItems } from "../utils/regionData5";

const Dropdown = ({
  label,
  items,
  type,
  value,
  onChange,
  isProjectDropdown,
  index,
  hintText,
  countryValue, // This should come from the context
  required = false,
  errorMessage = "This field is required",
  onSave,
  isPreview = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const dropdownRef = useRef(null);
  const dropdownButtonRef = useRef(null);
  const dropdownMenuRef = useRef(null);
  const contentRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [thumbHeight, setThumbHeight] = useState(40);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [startScrollTop, setStartScrollTop] = useState(0);
  const thumbRef = useRef(null);
  const [isTouched, setIsTouched] = useState(false); // Track if field has been touched
 // Validation function
 const isUserType = type === "USER TYPE";
 const validate = () => {
  if (!required) return true;
  
  if (type === "LANGUAGE" || type === "VALUE CHAIN" || type === "SMMEs") {
    return Array.isArray(value) && value.length > 0;
  } else {
    return value !== null && value !== undefined && value !== "";
  }
};
const isValid = validate();
const showError = required && isTouched && !isValid;
  // Calculate thumb height based on content height
  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    const updateThumbHeight = () => {
      const { scrollHeight, clientHeight } = content;
      const visibleRatio = clientHeight / scrollHeight;
      setThumbHeight(Math.max(visibleRatio * clientHeight, 20));
    };

    updateThumbHeight();
    window.addEventListener("resize", updateThumbHeight);
    return () => window.removeEventListener("resize", updateThumbHeight);
  }, [isOpen, items]);

  // Handle scroll events
  const handleScroll = () => {
    const content = contentRef.current;
    if (!content) return;

    const { scrollTop, scrollHeight, clientHeight } = content;
    const maxScrollTop = scrollHeight - clientHeight;
    const scrollRatio = scrollTop / maxScrollTop;
    const trackHeight = content.clientHeight - thumbHeight;
    setScrollTop(scrollRatio * trackHeight);
  };

  // Mouse down on thumb
  const handleThumbMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setIsDragging(true);
    setStartY(e.clientY);
    setStartScrollTop(contentRef.current.scrollTop);

    document.addEventListener("mousemove", handleThumbMouseMove);
    document.addEventListener("mouseup", handleThumbMouseUp);
    document.body.style.cursor = "grabbing";
  };

  // Mouse move when dragging thumb
  const handleThumbMouseMove = (e) => {
    if (!isDragging) return;

    const deltaY = e.clientY - startY;
    const content = contentRef.current;
    const { scrollHeight, clientHeight } = content;
    const maxScrollTop = scrollHeight - clientHeight;
    const trackHeight = clientHeight - thumbHeight;

    // Calculate new scroll position
    const scrollDelta = (deltaY / trackHeight) * maxScrollTop;
    const newScrollTop = Math.min(
      Math.max(0, startScrollTop + scrollDelta),
      maxScrollTop
    );

    content.scrollTop = newScrollTop;
  };

  // Mouse up when dragging thumb
  const handleThumbMouseUp = () => {
    setIsDragging(false);
    document.removeEventListener("mousemove", handleThumbMouseMove);
    document.removeEventListener("mouseup", handleThumbMouseUp);
    document.body.style.cursor = "";
  };

  // Click on track to jump to position
  const handleTrackClick = (e) => {
    const content = contentRef.current;
    if (!content || e.target === thumbRef.current) return;

    const track = e.currentTarget;
    const trackRect = track.getBoundingClientRect();
    const clickPosition = e.clientY - trackRect.top;
    const thumbCenter = thumbHeight / 2;
    const scrollRatio =
      (clickPosition - thumbCenter) / (trackRect.height - thumbHeight);

    const { scrollHeight, clientHeight } = content;
    const maxScrollTop = scrollHeight - clientHeight;
    content.scrollTop = scrollRatio * maxScrollTop;
  };

  const isValueChain = type === "VALUE CHAIN";
  const isSMMEs = type === "SMMEs";
  const isFARMING = type === "TYPE OF FARMING"; // Add this line

  const isLanguage = type === "LANGUAGE";
  const selectedValues = isValueChain || isFARMING
  ? Array.isArray(value)
    ? value
    : value
    ? [value]
    : []
  : value;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  const handleMouseEnter = () => {
    setIsOpen(true);
  };
  const   dropdownItems = {
    COUNTRY: [
      { name: "Algeria", iso: "DZ", code: "213" },
     { name: "Angola", iso: "AO", code: "244" },
     { name: "Benin", iso: "BJ", code: "229" },
     { name: "Botswana", iso: "BW", code: "267" },
     { name: "Burkina Faso", iso: "BF", code: "226" },
     { name: "Burundi", iso: "BI", code: "257" },
     { name: "Cabo Verde (Cape Verde)", iso: "CV", code: "238" },
     { name: "Cameroon", iso: "CM", code: "237" },
     { name: "Central African Republic", iso: "CF", code: "236" },
     { name: "Chad", iso: "TD", code: "235" },
     { name: "Comoros", iso: "KM", code: "269" },
     { name: "Congo (Brazzaville)", iso: "CG", code: "242" },
     { name: "Congo (Kinshasa/DRC)", iso: "CD", code: "243" },
     { name: "Côte d'Ivoire (Ivory Coast)", iso: "CI", code: "225" },
     { name: "Djibouti", iso: "DJ", code: "253" },
     { name: "Egypt", iso: "EG", code: "20" },
     { name: "Equatorial Guinea", iso: "GQ", code: "240" },
     { name: "Eritrea", iso: "ER", code: "291" },
     { name: "Eswatini (Swaziland)", iso: "SZ", code: "268" },
     { name: "Ethiopia", iso: "ET", code: "251" },
     { name: "Gabon", iso: "GA", code: "241" },
     { name: "Gambia", iso: "GM", code: "220" },
     { name: "Ghana", iso: "GH", code: "233" },
     { name: "Guinea", iso: "GN", code: "224" },
     { name: "Guinea-Bissau", iso: "GW", code: "245" },
     { name: "Kenya", iso: "KE", code: "254" },
     { name: "Lesotho", iso: "LS", code: "266" },
     { name: "Liberia", iso: "LR", code: "231" },
     { name: "Libya", iso: "LY", code: "218" },
     { name: "Madagascar", iso: "MG", code: "261" },
     { name: "Malawi", iso: "MW", code: "265" },
     { name: "Mali", iso: "ML", code: "223" },
     { name: "Mauritania", iso: "MR", code: "222" },
     { name: "Mauritius", iso: "MU", code: "230" },
     { name: "Morocco", iso: "MA", code: "212" },
     { name: "Mozambique", iso: "MZ", code: "258" },
     { name: "Namibia", iso: "NA", code: "264" },
     { name: "Niger", iso: "NE", code: "227" },
     { name: "Nigeria", iso: "NG", code: "234" },
     { name: "Rwanda", iso: "RW", code: "250" },
     { name: "São Tomé and Príncipe", iso: "ST", code: "239" },
     { name: "Senegal", iso: "SN", code: "221" },
     { name: "Seychelles", iso: "SC", code: "248" },
     { name: "Sierra Leone", iso: "SL", code: "232" },
     { name: "Somalia", iso: "SO", code: "252" },
     { name: "South Africa", iso: "ZA", code: "27" },
     { name: "South Sudan", iso: "SS", code: "211" },
     { name: "Sudan", iso: "SD", code: "249" },
     { name: "Tanzania", iso: "TZ", code: "255" },
     { name: "Togo", iso: "TG", code: "228" },
     { name: "Tunisia", iso: "TN", code: "216" },
     { name: "Uganda", iso: "UG", code: "256" },
     { name: "Zambia", iso: "ZM", code: "260" },
     { name: "Zimbabwe", iso: "ZW", code: "263" }
     ],
    REGION: [
      { name: "Gauteng", value: "01" },
    { name: "Eastern Cape", value: "02" },
    { name: "Limpopo", value: "03" },
    { name: "KwaZulu-Natal", value: "04" },
    { name: "Mpumalanga", value: "05" },
    { name: "Western Cape", value: "06" },
    { name: "North West", value: "07"},
    { name: "Northern Cape", value: "08" },
    { name: "Free State", value: "09" }
    ],
    
  
    DISTRICT: [
      { name: "City of Johannesburg", value: "(001)" },
      { name: "City of Tshwane", value: "(002)" },
      { name: "Ekurhuleni", value: "(003)" },
      { name: "Sedibeng", value: "(004)" },
      { name: "West Rand", value: "(005)" },
      { name: "Emfuleni", value: "(006)" },
      { name: "Lesedi Merafong City", value: "(007)" },
      { name: "Midvaal", value: "(009)" },
      { name: "Mogale City", value: "(010)" },
      { name: "Randfontein", value: "(011)" },
      { name: "Westonaria", value: "(012)" },
      { name: "Buffalo City Metropolitan", value: "(013)" },
      { name: "Nelson Mandela Bay", value: "(014)" },
      { name: "Alfred Nzo District", value: "(015)" },
      { name: "Amathole District", value: "(016)" },
      { name: "Cacadu District", value: "(018)" },
      { name: "Chris Hani District", value: "(019)" },
      { name: "Joe Gqabi", value: "(020)" },
      { name: "OR Tambo District", value: "(022)" },
      { name: "Amahlathi", value: "(023)" },
      { name: "Baviaans", value: "(024)" },
      { name: "Blue Crane Route", value: "(025)" },
      { name: "Buffalo City", value: "(026)" },
      { name: "Camdeboo", value: "(027)" },
      { name: "Elundini", value: "(028)" },
      { name: "Emalahleni", value: "(029)" },
      { name: "Engcobo", value: "(030)" },
      { name: "Gariep", value: "(031)" },
      { name: "Great Kei", value: "(032)" },
      { name: "Ikwezi", value: "(033)" },
      { name: "Ingquza Hill", value: "(034)" },
      { name: "Inkwanca", value: "(035)" },
      { name: "Intsika Yethu", value: "(036)" },
      { name: "Inxuba Yethemba", value: "(037)" },
      { name: "King Sabata Dalindyebo", value: "(038)" },
      { name: "Kou-Kamma", value: "(039)" },
      { name: "Kouga", value: "(040)" },
      { name: "Lukhanji", value: "(041)" },
      { name: "Makana", value: "(042)" },
      { name: "Maletswai", value: "(043)" },
      { name: "Matatiele", value: "(044)" },
      { name: "Mbhashe", value: "(045)" },
      { name: "Mbizana", value: "(046)" },
      { name: "Mhlontlo", value: "(047)" },
      { name: "Mnquma Local", value: "(048)" },
      { name: "Ndlambe Local", value: "(049)" },
      { name: "Ngqushwa", value: "(050)" },
      { name: "Nkonkobe", value: "(051)" },
      { name: "Ntabankulu", value: "(052)" },
      { name: "Nxuba Local", value: "(053)" },
      { name: "Nyandeni", value: "(054)" },
      { name: "Port St Johns", value: "(055)" },
      { name: "Sakhisizwe", value: "(056)" },
      { name: "Senqu Local", value: "(057)" },
      { name: "Sundays River Valley", value: "(058)" },
      { name: "Tsolwana Local", value: "(059)" },
      { name: "Umzimvubu", value: "(060)" },
      { name: "Capricorn", value: "(061)" },
      { name: "Mopani", value: "(062)" },
      { name: "Sekhukhune", value: "(063)" },
      { name: "Vhembe", value: "(064)" },
      { name: "Waterberg", value: "(065)" },
      { name: "Aganang", value: "(066)" },
      { name: "Ba-Phalaborwa", value: "(067)" },
      { name: "Bela-Bela", value: "(068)" },
      { name: "Blouberg", value: "(069)" },
      { name: "Elias Motsoaledi", value: "(070)" },
      { name: "Ephraim Mogale", value: "(071)" },
      { name: "Fetakgomo", value: "(072)" },
      { name: "Greater Giyani", value: "(073)" },
      { name: "Greater Letaba", value: "(074)" },
      { name: "Greater Tubatse", value: "(075)" },
      { name: "Greater Tzaneen", value: "(076)" },
      { name: "Lepelle-Nkumpi", value: "(077)" },
      { name: "Lephalale", value: "(078)" },
      { name: "Makhado", value: "(079)" },
      { name: "Makhuduthamaga", value: "(080)" },
      { name: "Maruleng", value: "(081)" },
      { name: "Modimolle", value: "(082)" },
      { name: "Mogalakwena", value: "(083)" },
      { name: "Molemole", value: "(084)" },
      { name: "Mookgophong", value: "(085)" },
      { name: "Musina", value: "(086)" },
      { name: "Mutale", value: "(087)" },
      { name: "Polokwane", value: "(088)" },
      { name: "Thabazimbi", value: "(089)" },
      { name: "Thulamela", value: "(090)" },
      { name: "eThekwini", value: "(091)" },
      { name: "Amajuba", value: "(092)" },
      { name: "iLembe", value: "(094)" },
      { name: "Sisonke", value: "(095)" },
      { name: "Ugu", value: "(096)" },
      { name: "uMkhanyakude", value: "(098)" },
      { name: "uMzinyathi", value: "(099)" },
      { name: "uThukela", value: "(100)" },
      { name: "uThungulu", value: "(101)" },
      { name: "Zululand", value: "(102)" },
      { name: "Abaqulusi", value: "(103)" },
      { name: "Dannhauser", value: "(104)" },
      { name: "eDumbe", value: "(105)" },
      { name: "eMadlangeni", value: "(106)" },
      { name: "Emnambithi/Ladysmith", value: "(107)" },
      { name: "Endumeni Local", value: "(108)" },
      { name: "Ezingoleni", value: "(109)" },
      { name: "Greater Kokstad", value: "(110)" },
      { name: "Hibiscus Coast", value: "(111)" },
      { name: "Hlabisa", value: "(112)" },
      { name: "Imbabazane", value: "(113)" },
      { name: "Impendle", value: "(114)" },
      { name: "Indaka", value: "(115)" },
      { name: "Ingwe", value: "(116)" },
      { name: "Jozini", value: "(117)" },
      { name: "KwaDukuza", value: "(118)" },
      { name: "KwaSani", value: "(119)" },
      { name: "Mandeni", value: "(120)" },
      { name: "Maphumulo", value: "(121)" },
      { name: "Mbonambi", value: "(122)" },
      { name: "Mkhambathini", value: "(123)" },
      { name: "Mpofana", value: "(124)" },
      { name: "Msinga", value: "(125)" },
      { name: "Mthonjaneni", value: "(127)" },
      { name: "Mtubatuba", value: "(128)" },
      { name: "Ndwedwe", value: "(129)" },
      { name: "Newcastle", value: "(130)" },
      { name: "Nkandla", value: "(131)" },
      { name: "Nongoma", value: "(132)" },
      { name: "Nquthu", value: "(133)" },
      { name: "Ntambanana", value: "(134)" },
      { name: "Okhahlamba", value: "(135)" },
      { name: "Richmond", value: "(136)" },
      { name: "The Big Five False Bay", value: "(137)" },
      { name: "Ubuhlebezwe", value: "(138)" },
      { name: "Ulundi", value: "(139)" },
      { name: "Umdoni", value: "(140)" },
      { name: "uMhlabuyalingana", value: "(141)" },
      { name: "uMhlathuze", value: "(142)" },
      { name: "uMlalazi", value: "(143)" },
      { name: "uMngeni", value: "(144)" },
      { name: "uMshwathi", value: "(145)" },
      { name: "Umtshezi", value: "(146)" },
      { name: "uMuziwabantu", value: "(147)" },
      { name: "Umvoti", value: "(148)" },
      { name: "Umzimkhulu", value: "(149)" },
      { name: "Umzumbe", value: "(150)" },
      { name: "uPhongolo", value: "(151)" },
      { name: "Vulamehlo", value: "(152)" },
      { name: "Ehlanzeni", value: "(153)" },
      { name: "Gert Sibande", value: "(154)" },
      { name: "Nkangala", value: "(155)" },
      { name: "Albert Luthuli", value: "(156)" },
      { name: "Bushbuckridge", value: "(157)" },
      { name: "Dipaleseng", value: "(158)" },
      { name: "Dr JS Moroka", value: "(159)" },
      { name: "Emakhazeni", value: "(160)" },
      { name: "Emalahleni", value: "(161)" },
      { name: "Lekwa", value: "(163)" },
      { name: "Mbombela", value: "(164)" },
      { name: "Mkhondo", value: "(165)" },
      { name: "Msukaligwa", value: "(166)" },
      { name: "Nkomazi", value: "(167)" },
      { name: "Pixley Ka Seme", value: "(168)" },
      { name: "Steve Tshwete", value: "(169)" },
      { name: "Thaba Chweu", value: "(170)" },
      { name: "Thembisile Hani", value: "(171)" },
      { name: "Umjindi", value: "(172)" },
      { name: "Victor Khanye", value: "(173)" },
      { name: "City of Cape Town", value: "(174)" },
      { name: "Cape Winelands", value: "(175)" },
      { name: "Central Karoo", value: "(176)" },
      { name: "Eden", value: "(177)" },
      { name: "Overberg", value: "(178)" },
      { name: "West Coast", value: "(179)" },
      { name: "Beaufort West", value: "(180)" },
      { name: "Bergrivier", value: "(181)" },
      { name: "Bitou", value: "(182)" },
      { name: "Breede Valley", value: "(183)" },
      { name: "Cape Agulhas", value: "(184)" },
      { name: "Cederberg", value: "(185)" },
      { name: "Drakenstein", value: "(186)" },
      { name: "George", value: "(187)" },
      { name: "Hessequa", value: "(188)" },
      { name: "Kannaland", value: "(189)" },
      { name: "Knysna", value: "(190)" },
      { name: "Laingsburg", value: "(191)" },
      { name: "Langeberg", value: "(192)" },
      { name: "Matzikama", value: "(193)" },
      { name: "Mossel Bay", value: "(194)" },
      { name: "Oudtshoorn", value: "(195)" },
      { name: "Overstrand", value: "(196)" },
      { name: "Prince Albert", value: "(197)" },
      { name: "Saldanha Bay", value: "(198)" },
      { name: "Stellenbosch", value: "(199)" },
      { name: "Swartland", value: "(200)" },
      { name: "Swellendam", value: "(201)" },
      { name: "Theewaterskloof", value: "(202)" },
      { name: "Witzenberg", value: "(203)" },
      { name: "Bojanala Platinum", value: "(204)" },
      { name: "Dr Kenneth Kaunda", value: "(205)" },
      { name: "Dr Ruth Segomotsi Mompati", value: "(206)" },
      { name: "Ngaka Modiri Molema", value: "(207)" },
      { name: "City of Matlosana", value: "(208)" },
      { name: "Ditsobotla", value: "(209)" },
      { name: "Greater Taung", value: "(210)" },
      { name: "Kagisano-Molopo", value: "(211)" },
      { name: "Kgetlengrivier", value: "(212)" },
      { name: "Lekwa-Teemane", value: "(213)" },
      { name: "Madibeng", value: "(214)" },
      { name: "Mahikeng", value: "(215)" },
      { name: "Mamusa", value: "(216)" },
      { name: "Maquassi Hills", value: "(217)" },
      { name: "Moretele", value: "(218)" },
      { name: "Moses Kotane", value: "(219)" },
      { name: "Naledi", value: "(220)" },
      { name: "Ramotshere Moiloa", value: "(221)" },
      { name: "Ratlou", value: "(222)" },
      { name: "Rustenburg", value: "(223)" },
      { name: "Tlokwe", value: "(224)" },
      { name: "Tswaing", value: "(225)" },
      { name: "Ventersdorp", value: "(226)" },
      { name: "Frances Baard", value: "(227)" },
      { name: "John Taolo Gaetsewe", value: "(228)" },
      { name: "Namakwa", value: "(229)" },
      { name: "Siyanda", value: "(231)" },
      { name: "Dikgatlong", value: "(232)" },
      { name: "Emthanjeni", value: "(233)" },
      { name: "Ga-Segonyana", value: "(234)" },
      { name: "Gamagara", value: "(235)" },
      { name: "Hantam", value: "(236)" },
      { name: "Joe Morolong", value: "(237)" },
      { name: "Kai !Garib", value: "(238)" },
      { name: "Kamiesberg", value: "(239)" },
      { name: "Kareeberg", value: "(240)" },
      { name: "Karoo Hoogland", value: "(241)" },
      { name: "Kgatelopele", value: "(242)" },
      { name: "Khái-Ma", value: "(243)" },
      { name: "//Khara Hais", value: "(244)" },
      { name: "!Kheis", value: "(245)" },
      { name: "Magareng", value: "(246)" },
      { name: "Mier", value: "(247)" },
      { name: "Nama Khoi", value: "(248)" },
      { name: "Phokwane", value: "(249)" },
      { name: "Renosterberg", value: "(250)" },
      { name: "Richtersveld", value: "(251)" },
      { name: "Siyancuma", value: "(252)" },
      { name: "Siyathemba", value: "(253)" },
      { name: "Sol Plaatje", value: "(254)" },
      { name: "Thembelihle", value: "(255)" },
      { name: "Tsantsabane", value: "(256)" },
      { name: "Ubuntu", value: "(257)" },
      { name: "Umsobomvu", value: "(258)" },
      { name: "Mangaung", value: "(259)" },
      { name: "Fezile Dabi", value: "(260)" },
      { name: "Lejweleputswa", value: "(261)" }
  
  ],
"SMMEs": [
  {
    "heading": "Smallholder Commercial Farms",
    "items": [
      {
        "label": "Small-scale farms operating for profit. Use improved inputs, some mechanization, and sell surplus to local markets. Often family-run but may hire seasonal labor. Example: A 5-hectare maize farm using hybrid seeds and selling to a local mill.",
        "value": "ESC"
      }
    ]
  },
  {
    "heading": "Medium-Scale Farms",
    "items": [
      {
        "label": "Larger landholding than smallholders (e.g., 10–100 hectares). Often mechanized and more capitalized. Supply supermarkets, processors, or export markets. Example: A 50-hectare rice farm with irrigation and a tractor, selling to a national rice brand.",
        "value": "EMS"
      }
    ]
  },
  {
    "heading": "Farmer Cooperatives / Producer Organizations",
    "items": [
      {
        "label": "Groups of farmers who pool resources to gain market power. May own equipment, storage, or transport collectively. Can register as SMEs and operate commercially. Example: A cooperative of 100 tomato farmers owning a cold storage unit.",
        "value": "EFC"
      }
    ]
  },
  {
    "heading": "Contract Farming SMEs",
    "items": [
      {
        "label": "Farmers or farm groups under formal agreements to supply crops/livestock to processors or exporters. Often receive inputs and training from buyers. Example: Poultry farmers supplying eggs to a food company under a contract.",
        "value": "ECF"
      }
    ]
  },
  {
    "heading": "Niche or Specialized Farms",
    "items": [
      {
        "label": "Farms focused on high-value or specialized production: Organic farms, Horticulture farms (vegetables, flowers, herbs), Aquaculture enterprises, Livestock or poultry SMEs, Beekeeping and honey production. Example: A small farm producing organic herbs for export.",
        "value": "ENS"
      }
    ]
  },
  {
    "heading": "Agri-Entrepreneurs with Integrated Operations",
    "items": [
      {
        "label": "These SMEs farm and also manage part of the value chain, such as: On-farm processing (e.g., dairy to yogurt), Direct-to-consumer sales (farm shops, online orders). Example: A mushroom farmer selling pre-packed mushrooms to urban grocers.",
        "value": "EAE"
      }
    ]
  },
  {
    "heading": "Youth or Women-led Farming Startups",
    "items": [
      {
        "label": "Often tech-savvy and innovative. Use climate-smart or digital farming tools. May focus on urban/peri-urban agriculture. Example: A woman-led hydroponic vegetable farm near a city.",
        "value": "EYW"
      }
    ]
  },
],
    "VALUE CHAIN": [
       "Maize", "Macadamia", "Nuts", "Avocados", "Fruits", "Sugar cane", "Potatoes", "Tomatoes", "Onions", "Meat", "Poultry", "Dairy", "Fish", "Bees", "Timber", "Clothing", "Textile", "Footwear", "Leather", "Construction", "Solar", "Wind", "Tourism", "ICT Services", "Waste Management", "Logistics", "Music", "Film", "Fashion", "Gaming"
    ],
    LANGUAGE: [
      "English", "French", "Portuguese", "Kiswahili",
    ],
  };
  const getCountryLanguages = () => {
    const countryName = (countryValue?.name || countryValue || "").toString();
    
    if (countryName.includes("South Africa")) {
      return [
        "Afrikaans",
        "English",
        "isiNdebele",
        "Sepedi",
        "Setswana",
        "Sesotho",
        "isiSwati",
        "Xitsonga",
        "isiZulu"
      ];
    } else if (countryName.includes("Namibia")) {
      return [
        "English",
        "Oshiwambo",
        "Afrikaans",
        "German",
        "Nama/Damara",
        "Herero"
      ];
    } else if (countryName.includes("Kenya")) {
      return [
        "Swahili",
        "English"
      ];
    } else if (countryName.includes("Botswana")) {
      return [
        "English",
        "Setswana"
      ];
    } else if (countryName.includes("Zimbabwe")) {
      return [
        "English",
        "Shona",
        "Ndebele"
      ];
    } else if (countryName.includes("Mozambique")) {
      return [
        "Portuguese",
        "Emakhuwa",
        "Xichangana"
      ];
    }
    
    // Default languages if country not specified
    return dropdownItems.LANGUAGE;
  };
  const UnselectedIcon = () => (
    <svg
      viewBox="0 0 17 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-[6px] h-[6px] md:w-[17px] md:h-[17px]"
    >
      <circle cx="8.5" cy="8.5" r="8" fill="#D9D9D9" stroke="none" />
    </svg>
  );
  
  const SelectedIcon = () => (
    <svg
      viewBox="0 0 17 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-[6px] h-[6px] md:w-[17px] md:h-[17px]"
    >
      <circle cx="8.5" cy="8.5" r="8" fill="#2E453D" stroke="#FFBD26" />
      <path
        d="M5 8.5L7.5 11L12 6"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
  
  const handleButtonClick = () => {
    setIsTouched(true);
    setIsOpen(!isOpen);
  };
  useEffect(() => {
    if (isTouched) {
      validate();
    }
  }, [value, isTouched]);
  // --- The main dynamic logic for region/district/municipality --- //
  // Pick correct data arrays based on selected country
// In the Dropdown component's getCurrentItems function:
// In your ReportsSection component, update the getCurrentItems function like this:
// Get current items based on country selection
function getCurrentItems() {
  if (type === "SMMEs") return dropdownItems?.SMMEs || [];
  if (type === "LANGUAGE") return getCountryLanguages() || [];
  
  const countryCode = countryValue?.code || "27"; // Default to South Africa
  
  if (type === "REGION") {
    return getRegionList(countryCode) || [];
  }
  if (type === "DISTRICT") {
    // You might need to pass the selected region value here if districts are region-specific
    return getDistrictList(countryCode) || [];
  }
  if (type === "MUNICIPALITY") {
    return getMunicipalityList(countryCode) || [];
  }
  
  return items || [];
}

  const currentItems = getCurrentItems();
  // Header title for dropdown
  const getHeaderTitle = () => {
    const countryName = (countryValue?.name || countryValue || "").toString();
    if (countryName.includes("Namibia")) {
      if (type === "REGION") return "Namibia Regions";
      if (type === "DISTRICT") return "Namibia Districts";
    } else if (countryName.includes("Kenya")) {
      if (type === "REGION") return "Kenya Counties";
      if (type === "DISTRICT") return "Kenya Districts";
    } else if (countryName.includes("South Africa")) {
      if (type === "REGION") return "South Africa Provinces";
      if (type === "DISTRICT") return "South Africa Districts";
    }
    return label;
  };

  // Multi-select for LANGUAGE
  
  // Multi-select for LANGUAGE
  const handleItemClick = (item) => {
    setSelected(item);
    setIsTouched(true);
    
    if (isLanguage || isFARMING || isValueChain) {
      // Multi-select logic for these types
      let newValues = Array.isArray(value) ? [...value] : [];
      
      // Normalize comparison (handle both string and object items)
      const itemValue = typeof item === 'object' ? item.value : item;
      const existingIndex = newValues.findIndex(v => 
        (typeof v === 'object' ? v.value : v) === itemValue
      );
  
      if (existingIndex >= 0) {
        // Remove if already selected
        newValues.splice(existingIndex, 1);
      } else {
        // Add if not selected
        newValues.push(item);
      }
      onChange(type, newValues);
      
      // Don't close the dropdown for these types
      if (!isLanguage) return;
    }
    else if (isValueChain) {
      // Existing multi-select logic for value chains
      const alreadySelected = selectedValues.some(
        (v) => {
          if (typeof v === "object" && typeof item === "object") {
            return v.value === item.value;
          }
          return v === item;
        }
      );
      let newSelectedValues;
      if (alreadySelected) {
        newSelectedValues = selectedValues.filter(
          (v) =>
            !(
              typeof v === "object" &&
              typeof item === "object" &&
              v.value === item.value
            ) && v !== item
        );
      } else {
        newSelectedValues = [...selectedValues, item];
      }
      onChange(type, newSelectedValues);
    } else {
      // Default single select behavior
      onChange(type, item);
      setIsOpen(false);
    }
  };
  const [selected, setSelected] = useState(items[0]); // Assuming 'items' is your dropdown options
  // Only show selected in button
   // Only show selected in button
   const getDisplayValue = () => {
    // If no value is selected yet, return "-"
    if (!value && !isLanguage && !isSMMEs && !isFARMING && !isValueChain) return "-";
  
    if ((isLanguage || isFARMING || isValueChain) && Array.isArray(value)) {
      if (value.length === 0) return "-";
      return value.map(v => typeof v === 'object' ? v.label || v.name || v.value : v).join(', ');
    }
    
    if (isSMMEs) {
      if (!value) return "-";
      const currentValue = Array.isArray(value) ? value[0] : value;
      if (currentValue) {
        if (typeof currentValue === 'string') return currentValue;
        const selectedSection = dropdownItems.SMMEs?.find(section => 
          section.items.some(item => item.value === currentValue.value)
        );
        return selectedSection?.heading || currentValue.label || "-";
      }
      return "-";
    }
    
    if (isFARMING && !value) return "-";
  
    if ((type === "REGION" || type === "DISTRICT") && countryValue) {
      const currentItems = getCurrentItems();
      const isValidSelection = currentItems.some(item => 
        (typeof item === 'object' && item.name === value?.name) || 
        (typeof item === 'string' && item === value)
      );
      if (!isValidSelection) return "-";
    }
  
    if (typeof value === "object" && value !== null) {
      return value.label || value.name || value.value || "-";
    }
    
    return value || "-";
  };
  const renderRegularItems = () => {
    return currentItems.map((item, index) => {
      const itemName = typeof item === "object" ? item.name : item;
      const itemValue = typeof item === "object" ? item.value : item;
      let isSelected = false;
  
      if ((isLanguage || isFARMING) && Array.isArray(value)) { // Add isFARMING here
        isSelected = value.includes(item);
      }
      else if (isValueChain && Array.isArray(selectedValues)) {
        isSelected = selectedValues.some(
          (v) =>
            (typeof v === "object" &&
              typeof item === "object" &&
              v.value === item.value) ||
            v === item
        );
      } else if (typeof value === "object" && typeof item === "object") {
        isSelected =
          (value.name && item.name && value.name === item.name) ||
          (value.label && item.label && value.label === item.label);
      } else if (typeof value === "string" && typeof item === "object") {
        isSelected =
          (item.name && value === item.name) ||
          (item.label && value === item.label);
      } else if (typeof value === "object" && typeof item === "string") {
        isSelected =
          (value.name && value.name === item) ||
          (value.label && value.label === item);
      } else {
        isSelected = value === item;
      }

      // Show code for REGION and MUNICIPALITY (besides name)
      const showCode =
      (type === "REGION" || type === "DISTRICT") &&
      typeof item === "object" &&
      item.value !== undefined;
        function capitalizeWords(str) {
          return str.replace(/\b\w/g, char => char.toUpperCase());
        }
        
        return (
          <li
            key={index}
            onMouseEnter={() => setHoveredItem(itemName)}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleItemClick(item);
            }}
            className="flex justify-between items-center px-1 md:px-3 py-1 md:py-2 rounded-md hover:bg-gray-100 cursor-pointer relative"
          >
         <span className="text-black text-[4px] md:text-[12px] flex flex-col flex-1 mr-2 whitespace-normal break-words">
  {itemName}
  {showCode && (
    <span className="text-gray-500 text-[4px] md:text-[14px] block">
      {itemValue}
    </span>
  )}
</span>
         <div className="flex items-center">
  {isSelected ? <SelectedIcon /> : <UnselectedIcon />}
</div>
          </li>
        );
    });
  };
  const renderGroupedItems = () => {
    if (!currentItems || !Array.isArray(currentItems)) return null;
  
    return currentItems.map((section, sectionIndex) => (
      <React.Fragment key={sectionIndex}>
        <li className="text-[4px] md:text-[14px] font-bold bg-gray-100 px-1 md:px-3 py-1 sticky top-0 z-10">
          {section.heading}
        </li>
        {section.items.map((item, itemIndex) => {
          // Normalize item to always be an object
          const normalizedItem = typeof item === 'string' 
            ? { label: item, value: item } 
            : item;
  
          // Check if this item is selected
          const isSelected = Array.isArray(value)
            ? value.some(v => 
                (typeof v === 'object' ? v.value : v) === 
                (typeof normalizedItem === 'object' ? normalizedItem.value : normalizedItem)
              )
            : false;
  
          return (
            <li
              key={`${sectionIndex}-${itemIndex}`}
              onMouseEnter={() => setHoveredItem(normalizedItem.label || normalizedItem)}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleItemClick(normalizedItem);
              }}
              className="flex justify-between items-center px-[3px] md:px-3 py-1 md:py-2 rounded-md hover:bg-gray-100 cursor-pointer relative"
            >
              <span className="text-black text-[4px] md:text-[10px] flex-1 mr-2 whitespace-normal break-words">
                {normalizedItem.label}
              </span>
              <div className="flex items-center">
                {isSelected ? <SelectedIcon /> : <UnselectedIcon />}
              </div>
            </li>
          );
        })}
      </React.Fragment>
    ));
  };
  // Render SMMEs items (grouped)
  const renderSMMEsItems = () => {
    if (!currentItems || !Array.isArray(currentItems)) return null;

    return currentItems.map((section, sectionIndex) => (
      <React.Fragment key={sectionIndex}>
        <li className="text-[4px] md:text-[14px] font-bold bg-gray-100 px-1 md:px-3 py-1 sticky top-0 z-10">
          {section.heading}
        </li>
        {section.items.map((item, itemIndex) => {
          const isSelected = value && typeof value === "object" && value.value === item.value;
  
          return (
            <li
              key={`${sectionIndex}-${itemIndex}`}
              onMouseEnter={() => setHoveredItem(item.label)}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleItemClick(item);
              }}
              className="flex justify-between items-center px-[3px] md:px-3 py-1 md:py-2 rounded-md hover:bg-gray-100 cursor-pointer relative"
            >
<span className="text-black text-[4px] md:text-[14px] flex-1 mr-2 whitespace-normal break-words">
  {item.label}
  <span className="text-gray-500 text-[4px] md:text-[14px] block">
    {item.value}
  </span>
</span>
            <div className="flex items-center">
  {isSelected ? <SelectedIcon /> : <UnselectedIcon />}
</div>
            </li>
          );
        })}
      </React.Fragment>
    ));
  };
  function capitalizeWords(str) {
    return str.replace(/\b\w/g, char => char.toUpperCase());
  }
  
  return (
    <div
      className="flex flex-col w-full md:mt-0 mt-1 relative md:mb-3"
      style={{ position: "relative", zIndex: isOpen ? 100 : 1 }}
      ref={dropdownRef}
    >
      <label className="md:text-[20px] font-bold text-[5px] text-black mb-1 capitalize truncate">
      {label}
      </label>
      {showError && (
          <span className="text-red-500 text-[3px] md:text-[12px]">
            {errorMessage}
          </span>
        )}
      <div
        className="flex relative z-20 w-full"
        ref={dropdownButtonRef}
        onMouseEnter={handleMouseEnter}
        onClick={() => setIsOpen(!isOpen)}
      >
<button
  className={`relative xl:w-[168px] lg:w-[140px] md:w-[100px] w-full h-[20px] md:h-[53px] ${
    isProjectDropdown ? "bg-[#2E453D]" : "bg-[#2E453D]"
  } text-white flex justify-between items-center text-[4px] md:text-[17px] px-[2px] rounded-l-[4px] truncate`}
>
  <span className={`whitespace-normal break-words font-bold leading-[0.90] text-center capitalize w-full ${
    getDisplayValue() === "-" ? "text-gray-400" : ""
  }`}>
    {capitalizeWords(getDisplayValue())}
  </span>
  
  {/* This will show the code in the bottom right corner */}
  <span className="absolute bottom-[1px] right-3 text-[3px] md:text-[8px] text-white">
    {(type === "COUNTRY" && value?.code) || 
     (type === "REGION" && value?.value) || 
     (type === "DISTRICT" && value?.value) || 
     (type === "SMMEs" && value?.value) || 
     '-'}
  </span>
</button>
<div
  className={`w-[16px] md:w-[37px] h-[20px] md:h-[53px] ${
    (type === "REGION" || type === "DISTRICT")  // Changed from DISTRICT to REGION
      ? "bg-[#FDBB26]" 
      : isProjectDropdown 
        ? "bg-[#152C24]" 
        : "bg-[#152C24]"
  } flex justify-center items-center rounded-r-[4px] -ml-1 z-30`}
  onClick={() => setIsOpen(!isOpen)}
>
  <img
    src="https://res.cloudinary.com/dzngpgki4/image/upload/v1745014174/Expand_down_3_ggzyur.png"
    alt="expand"
    className="w-2 h-2 md:w-5 md:h-[24px]"
  />
</div>
      </div>
      <p className={`text-[4px] md:text-[14px] mt-[2px] truncate ${
        showError ? "text-red-500" : "text-black"
      }`}>
        {showError ? errorMessage : ""}
      </p>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={dropdownMenuRef}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-full mt-1 left-0 w-full md:w-[190px] border border-[#979797]/80 bg-white rounded shadow-lg p-[0px] md:p-2 max-h-[160px] md:max-h-[400px] flex flex-col"
            style={{
              position: "absolute",
              zIndex: 1000,
              backgroundColor: "white",
            }}
            onMouseEnter={() => setIsOpen(true)}
            onMouseLeave={() => setIsOpen(false)}
          >
            <li className="flex items-center justify-between text-[4px] px-1 md:px-3 pt-1 md:text-[14px] font-semibold md:mb-2">
              {getHeaderTitle()}
              <img
                src="https://res.cloudinary.com/dzngpgki4/image/upload/v1750034880/Export_1_vurzim.png"
                alt="icon"
                className="md:w-[30px] md:h-[30px] h-[8px] w-[8px] cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                }}
              />
            </li>

            {/* Show column headers for REGION and MUNICIPALITY */}
           {/* {(type === "REGION" || type === "DISTRICT") && countryValue && (
  <li className="flex justify-between items-center px-1 md:px-3 py-1">
    <span className="text-[4px] md:text-[14px] font-bold">
      Name
    </span>
    <span className="text-[4px] md:text-[14px] font-bold">
      Code
    </span>
  </li>
)} */}

            <div className="w-full h-5 flex justify-end">
              <img
                src="https://res.cloudinary.com/dzngpgki4/image/upload/v1747461105/Expand_up_mflvtu.png"
                alt="scroll-up"
                className="md:w-4 md:h-4 h-2 w-2"
                onClick={() => {
                  contentRef.current.scrollBy({
                    top: -50,
                    behavior: "smooth",
                  });
                }}
              />
            </div>

            <div className="custom-scroll-area">
              <div
                className="custom-scroll-content"
                ref={contentRef}
                onScroll={handleScroll}
              >
             <ul className="md:px-2 ">
  {type === "SMMEs" || type === "TYPE OF FARMING" 
    ? renderGroupedItems()
    : renderRegularItems()}
</ul>
              </div>
              <div className="custom-scroll-track" onClick={handleTrackClick}>
                <div
                  ref={thumbRef}
                  className="custom-scroll-thumb"
                  style={{
                    height: `${thumbHeight}px`,
                    top: `${scrollTop}px`,
                    cursor: isDragging ? "grabbing" : "grab",
                  }}
                  onMouseDown={handleThumbMouseDown}
                ></div>
              </div>
            </div>

            <div className="w-full h-5 flex justify-end">
              <img
                src="https://res.cloudinary.com/dzngpgki4/image/upload/v1747461085/Expand_down_vbf4bb.png"
                alt="scroll-down"
                className="md:w-4 md:h-4 h-2 w-2"
                onClick={() => {
                  contentRef.current.scrollBy({
                    top: 50,
                    behavior: "smooth",
                  });
                }}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <p className="text-[4px] md:text-[14px] mt-[2px] text-black truncate">
        {hintText}
      </p>

      <style jsx>{`
      .filter-yellow {
    filter: brightness(0) saturate(100%) invert(77%) sepia(57%) saturate(675%) hue-rotate(340deg) brightness(102%) contrast(96%);
  }
        .custom-scroll-area {
          position: relative;
          max-height: 200px;
          display: flex;
          overflow: hidden;
        }

        .custom-scroll-content {
          flex: 1;
          overflow-y: scroll;
          scrollbar-width: none;
          -ms-overflow-style: none;
          padding-right: 1px;
        }

        .custom-scroll-content::-webkit-scrollbar {
          display: none;
        }

        .custom-scroll-track {
          width: 14px;
          background: #e7ebeb;
          border-radius: 3px;
          position: relative;
          margin-left: 4px;
          cursor: pointer;
        }

        .custom-scroll-thumb {
          width: 100%;
          background: #979797;
          border-radius: 3px;
          position: absolute;
          right: 0;
          transition: background 0.2s;
        }

        .custom-scroll-thumb:hover {
          background: #444;
        }

        @media (max-width: 768px) {
          .custom-scroll-track {
            width: 4px;
            margin-left: 2px;
          }
            .custom-scroll-area {
          position: relative;
          max-height: 150px;
          display: flex;
          overflow: hidden;
          width: 100%;
        }
              .custom-scroll-content {
          flex: 1;
          overflow-y: scroll;
          scrollbar-width: none;
          -ms-overflow-style: none;
          padding-right: 0px;
        }

        }
      `}</style>
    </div>
  );
};
const DROPDOWN_LABELS = {
  COUNTRY: "Country",
  REGION: "REGION",
  DISTRICT: "AREA", // Changed from "District" to "Area"
  "VALUE CHAIN": "VALUE CHAIN",
  LANGUAGE: "Language",
  "USER TYPE": "USER",
  "TYPE OF FARMING": "FARMING",

  SMMEs: "SMME",
};


// Add this function to get hint text for each field
const getHintText = (type) => {
  switch(type) {
    case "USER TYPE":
      return "Select your USER TYPE";
    case "REGION":
      return "Select just one option.";
    case "DISTRICT":
      return "Select just one option.";
    case "VALUE CHAIN":
      return "Select relevant value chains";
    case "TYPE OF FARMING":
      return "Select preferred languages";
    case "SMMEs":
      return "Select your farming type";
    default:
      return "";
  }
};
const ReportsSection = ({ values, onChange, isPreview = false, onSave }) => {
  const { selectedCountry } = useCountry();
  
  const [validationError, setValidationError] = useState("");
  
  const validateSelections = () => {
    const requiredFields = ['REGION', 'DISTRICT', 'SMMEs', 'USER TYPE'];
    const missingFields = requiredFields.filter(field => 
      !values[field] || 
      values[field] === "-" || 
      (typeof values[field] === 'object' && !values[field].name && !values[field].value)
    );

    if (missingFields.length > 0) {
      setValidationError("*Select your Region, Area, SMMEs Type of farming and user type");
      return false;
    }
    
    setValidationError("");
    return true;
  };

  const initializedValues = React.useMemo(() => {
    const defaults = {
      COUNTRY: selectedCountry,
      ...values
    };
    
    Object.keys(dropdownItems).forEach((key) => {
      if (key !== "COUNTRY" && (values[key] === undefined || values[key] === null)) {
        if (key === "SMMEs" || key === "TYPE OF FARMING") {
          defaults[key] = null; // Initialize as null (no selection)
        } else if (Array.isArray(dropdownItems[key])) {
          // Handle other array types
        } else {
          defaults[key] = 
            typeof dropdownItems[key][0] === 'object'
              ? dropdownItems[key][0]
              : dropdownItems[key][0];
        }
      }
    });
    return defaults;
  }, [values, selectedCountry]);
  return (
    <div className="w-full md:mb-2 md:mt-2 mt-5 mb-3 font-Inter mx-auto px-1">
      <div className="flex md:flex-unwrap flex-nowrap gap-[2px] md:gap-[3px] w-full">
        {Object.keys(dropdownItems).map((type) => {
          return (
            <div key={type} className="w-[16.6%] min-w-[50px] md:w-auto">
            {isPreview ? (
  <div className="flex flex-col">
    <label className="md:text-[20px] font-bold text-[5px] text-black mb-1 capitalize truncate">
      {DROPDOWN_LABELS[type] || type}
    </label>
    <div className="flex relative z-20 w-full">
      <button
        className={`relative xl:w-[168px] lg:w-[140px] md:w-[100px] w-full h-[20px] md:h-[53px] bg-[#2E453D] text-white flex justify-between items-center text-[4px] md:text-[17px] px-[2px] rounded-l-[4px] truncate`}
      >
        <span className={`whitespace-normal break-words font-bold leading-[0.90] text-center capitalize w-full`}>
          {typeof values[type] === 'object' 
            ? values[type]?.label || values[type]?.name || values[type]?.value || "-"
            : values[type] || "-"}
        </span>
        {typeof values[type] === 'object' && values[type]?.value && (
          <span className="absolute bottom-[1px] right-3 text-[3px] md:text-[8px] text-white">
            {values[type].value}
          </span>
        )}
      </button>
      <div
        className={`w-[16px] md:w-[37px] h-[20px] md:h-[53px] ${
          // Change first two dropdowns to yellow, others keep original color
          (type === 'REGION' || type === 'DISTRICT') 
            ? 'bg-yellow-500' 
            : 'bg-[#152C24]'
        } flex justify-center items-center rounded-r-[4px] -ml-1 z-30`}
      >
      </div>
    </div>
  </div>
) : (
 
  <Dropdown
    label={DROPDOWN_LABELS[type] || type}
    items={dropdownItems[type]}
    type={type}
    value={values[type]}
    onChange={onChange}
    countryValue={selectedCountry}
    hintText={getHintText(type)}
  />
)}
            </div>
          );
        })}
      </div>
      {validationError && (
        <div className="text-red-500 text-[10px] md:text-[14px] mt-2">
          {validationError}
        </div>
      )}
    </div>
  );
};

export default ReportsSection;